import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './FilmScores.module.css';
import ReactPlayer from 'react-player';
import { v4 as uuidv4 } from 'uuid';
import { IoCloseOutline } from "react-icons/io5";
import { trackVideoInteraction, trackNavigation, trackButtonClick, trackModal } from '../utils/analytics';

const videos = [
  {
    id: 1,
    title: 'Lilly Lives Alone',
    pageLink: '/lilly',
    thumbnail: 'https://benjamincleek.s3.us-west-2.amazonaws.com/photos/bd85e04b-7c11-4c18-a89b-60bc41fe50aa-Untitled%20drawing.jpg',
    director: 'Martin Melnick',
    description: 'Guided by the ghost of her daughter, a guilt-ridden mother plunges into madness through a drug-fueled fever dream.',
  },
  {
    id: 2,
    title: 'Two Funerals and a Freezer',
    pageLink: '/tfaaf',
    thumbnail: 'https://benjamincleek.s3.us-west-2.amazonaws.com/photos/876dd415-e840-4396-a407-7a59fe7972a7-TFAAF%20-%20Two%20Funerals%20poster%20-%20smaller.jpg',
    director: 'Melissa Fischer',
    description: 'A Rutkus film about grief, memory, and the family secrets that surface long after loss.',
  },
  {
    id: 3,
    title: 'The Mad Ones',
    videoUrl: 'https://vimeo.com/685250353',
    thumbnail: 'https://benjamincleek.s3.us-west-2.amazonaws.com/photos/3e1f11f2-fd80-4522-88c2-16a36eb12e6b-The%20Mad%20Ones.jpg',
    director: 'Aniruddh Pandit',
    description: 'A touching comedy about an unlikely friendship that grows between three twenty-something professionals searching for meaning in life and work in the digital age.',
  },
  {
    id: 4,
    title: 'Alma 2020',
    pageLink: '/alma',
    thumbnail: 'https://benjamincleek.s3.us-west-2.amazonaws.com/photos/8c352e07-0ee8-46b4-ab1b-6296a72d539f-Screen%20Shot%202024-08-03%20at%202.05.03%20PM.png',
    description: 'A mysterious traveler crash-lands on an unknown planet and, against all odds, can\'t help but have a good time.',
  },
  {
    id: 5,
    title: 'Spring',
    videoUrl: 'https://www.youtube.com/watch?v=732w9e0MXzA&t=11s',
    thumbnail: 'https://benjamincleek.s3.us-west-2.amazonaws.com/photos/1998ce28-1e24-40c4-a990-b8e6602664d7-Spring%20-%20awarded.jpg',
    description: 'A young girl and her dog brave dangerous obstacles to reach the tree of life and restore glory to the forest.',
  },
  {
    id: 6,
    title: 'Evolution of Evil',
    videoUrl: 'https://www.youtube.com/watch?v=-CXvgPWSKdc',
    thumbnail: 'https://benjamincleek.s3.us-west-2.amazonaws.com/photos/75f81d2a-56f8-4bbf-b9f2-d17ed206275d-Evolution%20of%20Evil%20%281%29.jpg',
    director: 'Kevin Forest',
    description: 'A couple encounters unexpected terror while camping in the forests of the Pacific Northwest, and discover that getting away from it all can be more dangerous than they ever imagined.',
  },
  {
    id: 7,
    title: 'Common Threads',
    videoUrl: 'https://vimeo.com/14141165',
    thumbnail: 'https://benjamincleek.s3.us-west-2.amazonaws.com/photos/6a213956-0055-407a-a09d-171ef569344d-Common%20Threads.jpg',
    director: 'Amber Ward',
    description: 'Two trauma therapists break more than half a century of silence — a feature documentary about surviving abuse, healing, and the long work of becoming whole.',
  },
  {
    id: 8,
    title: 'Sliding Scale',
    videoUrl: 'https://www.youtube.com/watch?v=-YEDn6mSKJg',
    thumbnail: 'https://benjamincleek.s3.us-west-2.amazonaws.com/photos/8b168917-1d1f-49c4-b9e9-e57b937c1f24-Sliding%20Scale%20thumb.jpg',
    director: 'Aniruddh Pandit',
    description: 'A therapist introduces a patient to hyp-yes-is — but what begins as positive thinking quickly unravels into something far more unthinkable.',
  },
  {
    id: 9,
    title: 'House of Greeting Cards',
    videoUrl: 'https://vimeo.com/130605743',
    thumbnail: 'https://benjamincleek.s3.us-west-2.amazonaws.com/photos/c73f22db-51ab-4ec4-9661-9ac50c3537f3-House%20Of%20Greeting%20Cards-fotor-20240215103921.jpg',
    director: 'Aniruddh Pandit',
    description: 'A cunning greeting card clerk has one last Dads & Grads cycle to outmaneuver his competition — or sabotage them. Because retail, it turns out, is just politics with better lighting.',
  },
  {
    id: 10,
    title: 'ECT',
    videoUrl: 'https://www.youtube.com/watch?v=9iuB0r2UQvg',
    thumbnail: 'https://benjamincleek.s3.us-west-2.amazonaws.com/photos/938ab1d1-e8a8-4ee5-81ce-e59f3c814396-ECT%20poster%20square.jpg',
    director: 'Laura Pirano',
    description: 'Narrated by Kitty Dukakis, this hand-watercolored animated short works to destigmatize electroconvulsive therapy — tracing her personal battle with treatment-resistant depression and the treatment that finally brought relief.',
  },
];

  const VideoCard = ({ video, onClick }) => (
    <div
      className={styles.videoCard}
      onClick={(event) => onClick(video, event)}
    >
      <div className={styles.imageWrapper}>
        <img className={styles.videoImg} src={video.thumbnail} alt={video.title} />
      </div>
      <div className={styles.cardInfo}>
        <p className={styles.cardTitle}>{video.title}</p>
        {video.director && <p className={styles.cardDirector}>dir. {video.director}</p>}
        {video.description && <p className={styles.cardDescription}>{video.description}</p>}
      </div>
    </div>
  );

  const Composer = () => {
    const navigate = useNavigate();
    const [selectedVideo, setSelectedVideo] = useState(null);
    const [isPlayerVisible, setPlayerVisible] = useState(false);
    const playerRef = useRef(null);
    const enteredFsRef = useRef(false);

    const requestMobileFullscreen = () => {
      if (!window.matchMedia?.('(pointer: coarse)').matches) return;
      const el = document.documentElement;
      const fsReq = el.requestFullscreen ?? el.webkitRequestFullscreen;
      if (fsReq) {
        enteredFsRef.current = true;
        fsReq.call(el).then(() => screen.orientation?.unlock?.()).catch(() => {
          enteredFsRef.current = false;
        });
      }
    };

    const exitMobileFullscreen = () => {
      if (!enteredFsRef.current) return;
      enteredFsRef.current = false;
      (document.exitFullscreen ?? document.webkitExitFullscreen)?.call(document);
    };

    const handleVideoClick = (video, event) => {
      if (video.pageLink) {
        navigate(video.pageLink);
      } else {
        if (!selectedVideo || selectedVideo.id !== video.id) {
          setSelectedVideo(video);
          setPlayerVisible(true);
          requestMobileFullscreen();
        } else {
          exitMobileFullscreen();
          setSelectedVideo(null);
          setPlayerVisible(false);
        }
        event.stopPropagation();
      }
    };

    const handleCloseModal = () => {
      if (selectedVideo) {
        trackVideoInteraction('close', selectedVideo.title, 'FilmScores');
        trackModal('close', `FilmScores - ${selectedVideo.title}`);
      }
      exitMobileFullscreen();
      setPlayerVisible(false);
      setSelectedVideo(null);
    };

    useEffect(() => {
      const handleClickOutside = (event) => {
        if (
          playerRef.current &&
          !playerRef.current.contains(event.target) &&
          event.target.className !== styles.button &&
          event.target.closest(`.${styles.modalContent}`) === null
        ) {
          handleCloseModal();
        }
      };

      document.addEventListener('mousedown', handleClickOutside);

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [playerRef]);

    useEffect(() => {
      const onFsChange = () => {
        if (!(document.fullscreenElement ?? document.webkitFullscreenElement) && enteredFsRef.current) {
          enteredFsRef.current = false;
          setPlayerVisible(false);
          setSelectedVideo(null);
        }
      };
      document.addEventListener('fullscreenchange', onFsChange);
      document.addEventListener('webkitfullscreenchange', onFsChange);
      return () => {
        document.removeEventListener('fullscreenchange', onFsChange);
        document.removeEventListener('webkitfullscreenchange', onFsChange);
      };
    }, []);
  
    return (
      <div className={styles.filmScores}>
        <div className={styles.videoThumbs}>
          {videos.map((video) => (
            <VideoCard key={uuidv4()} video={video} onClick={handleVideoClick} />
          ))}
          {selectedVideo && (
            <div className={styles.modal}>
              <div className={styles.modalContent} ref={playerRef}>
                {!selectedVideo.pageLink && (
                  <ReactPlayer
                    url={selectedVideo.videoUrl}
                    width="100%"
                    height="100%"
                    style={{ position: 'absolute', top: 0, left: 0 }}
                    controls
                    playing
                  />
                )}
                <button 
                  onClick={handleCloseModal} 
                  className={styles.button}
                  onMouseDown={(e) => {
                    trackButtonClick('close_modal', 'FilmScores', { video_title: selectedVideo?.title });
                  }}
                >
                  <IoCloseOutline />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };
  
  export default Composer;